import { useState, memo } from 'react';

/**
 * Inline event card for chat messages.
 * Renders structured event data from the events fast path.
 * Matches v2 Linear-inspired mockup (docs/mockups/event-card-v2-linear.html).
 */
function EventCard({ event }) {
  const [expanded, setExpanded] = useState(false);

  if (!event || !event.title) return null;

  const isInPerson = event.format_label === 'in-person';
  const isEthics = event.has_ethics_credit;
  const isFree = event.price_label === 'Free';

  // Date badge: parse "Apr 25" into month + day
  const dateParts = (event.date_badge || '').split(' ');
  const month = dateParts[0] || null;
  const day = dateParts[1] || null;

  const monthColor = isEthics
    ? 'text-violet-600'
    : isInPerson
      ? 'text-orange-700'
      : 'text-[#104A4F]';

  // Credit + category display
  const categoryDisplay = (event.credit_categories || [])
    .filter((c) => c)
    .map((c) => c.charAt(0).toUpperCase() + c.slice(1))
    .join(' + ');

  const metaParts = [event.credits_label, categoryDisplay || null, event.price_label].filter(
    Boolean,
  );

  // Instructor initials
  const initials = event.instructor
    ? event.instructor
        .split(/[\s,]+/)
        .filter((w) => w && /^[A-Z]/.test(w))
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
    : null;

  const instructorName = event.instructor ? event.instructor.split(' — ')[0].trim() : null;
  const instructorOrg =
    event.instructor && event.instructor.includes(' — ')
      ? event.instructor.split(' — ').slice(1).join(' — ').trim()
      : event.vendor || null;

  const additionalCount = (event.additional_dates || []).length;
  const summary = typeof event.summary === 'string' ? event.summary.trim() : '';
  const scheduleLabel = [event.date_label, event.time_label].filter(Boolean).join(' · ');
  const supportingLine = [event.vendor, event.location].filter(Boolean).join(' · ');
  const linkCta =
    event.format_label === 'virtual'
      ? 'Open course'
      : event.format_label === 'in-person'
        ? 'View details'
        : 'Open offering';

  const formatLabel =
    event.format_label === 'virtual'
      ? 'Virtual'
      : event.format_label === 'in-person'
        ? 'In-Person'
        : event.format_label === 'hybrid'
          ? 'Hybrid'
          : event.format_label;

  return (
    <div
      className={`bg-white border rounded-[14px] overflow-hidden transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-[1px] hover:shadow-[0_8px_24px_rgba(15,23,42,0.07)] ${
        isEthics
          ? 'border-violet-300 hover:border-violet-400'
          : isInPerson
            ? 'border-orange-300 hover:border-orange-400'
            : 'border-[#A8DDE3] hover:border-[#5BBEC9]'
      }`}
    >
      {/* Clickable top area: header + tags + location — toggles expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left cursor-pointer focus:outline-none"
      >
        {/* Header: date + title + meta */}
        <div className="flex gap-3 p-3.5 pb-2.5 sm:p-4 sm:pb-3">
          {month && day ? (
            <div className="flex-shrink-0 w-11 text-center pt-0.5">
              <div
                className={`text-[11px] font-semibold uppercase tracking-wide leading-none ${monthColor}`}
              >
                {month}
              </div>
              <div className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#1A1D21] leading-tight">
                {day}
              </div>
            </div>
          ) : (
            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center text-[#8B919E]">
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1D21] leading-snug">{event.title}</div>
            {scheduleLabel && (
              <div className="mt-1 text-[12px] font-medium text-[#104A4F]">{scheduleLabel}</div>
            )}
            {metaParts.length > 0 && (
              <div className="text-xs text-[#5E6470] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                {metaParts.map((part, i) => (
                  <span key={i}>
                    {i > 0 && <span className="text-[#E4E7EA] mr-2">&middot;</span>}
                    {part}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 px-3.5 pb-2.5 sm:px-4 sm:pb-3">
          {formatLabel && (
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded ${
                isInPerson
                  ? 'bg-orange-50 text-orange-700'
                  : 'bg-[rgba(16,74,79,0.06)] text-[#104A4F]'
              }`}
            >
              {formatLabel}
            </span>
          )}
          {event.level && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-gray-100 text-[#5E6470] capitalize">
              {event.level}
            </span>
          )}
          {isEthics && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-violet-50 text-violet-600">
              Ethics Credit
            </span>
          )}
          {isFree && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">
              Free
            </span>
          )}
        </div>

        {summary && !expanded && (
          <div className="px-3.5 pb-3 text-[13px] leading-5 text-[#5E6470] sm:px-4">
            <p className="line-clamp-2">{summary}</p>
          </div>
        )}

        {supportingLine && (
          <div className="flex items-center gap-1.5 px-3.5 pb-2.5 sm:px-4 sm:pb-3 text-xs text-[#8B919E]">
            <svg
              className={`w-3.5 h-3.5 flex-shrink-0 ${isInPerson ? 'text-orange-700' : 'text-[#5BBEC9]'}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.274 1.765 11.307 11.307 0 00.757.433c.113.058.2.1.281.14l.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                clipRule="evenodd"
              />
            </svg>
            <span className="truncate">{supportingLine}</span>
          </div>
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-[#E4E7EA] p-3.5 sm:p-4 space-y-3.5">
          {summary && <div className="text-[13px] leading-6 text-[#4B5563]">{summary}</div>}

          {/* Pricing grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-x-5">
            {event.price_label && event.price_label !== 'Free' && (
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wide text-[#8B919E]">
                  Member
                </div>
                <div className="text-[13px] font-semibold text-[#104A4F]">
                  {event.price_label.replace(' member', '')}
                </div>
              </div>
            )}
            {event.price_nonmember && (
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wide text-[#8B919E]">
                  Non-Member
                </div>
                <div className="text-[13px] text-[#1A1D21]">{event.price_nonmember}</div>
              </div>
            )}
            {event.prerequisites && (
              <div className={event.prerequisites.length > 30 ? 'col-span-2' : ''}>
                <div className="text-[11px] font-medium uppercase tracking-wide text-[#8B919E]">
                  Prerequisites
                </div>
                <div className="text-[13px] text-[#1A1D21]">{event.prerequisites}</div>
              </div>
            )}
            {event.vendor && (
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wide text-[#8B919E]">
                  Vendor
                </div>
                <div className="text-[13px] text-[#1A1D21]">{event.vendor}</div>
              </div>
            )}
          </div>

          {/* Topics */}
          {event.topics && event.topics.length > 0 && (
            <div className="pt-3.5 border-t border-[#E4E7EA]">
              <div className="text-[11px] font-medium uppercase tracking-wide text-[#8B919E] mb-1.5">
                What You'll Learn
              </div>
              <ul className="space-y-0.5">
                {event.topics.map((topic, i) => (
                  <li
                    key={i}
                    className="text-[13px] text-[#5E6470] pl-3.5 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:w-1 before:h-1 before:rounded-full before:bg-[#E4E7EA]"
                  >
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructor */}
          {instructorName && (
            <div className="pt-3.5 border-t border-[#E4E7EA]">
              <div className="flex items-center gap-2.5">
                {initials && (
                  <div className="w-[34px] h-[34px] rounded-full bg-[rgba(16,74,79,0.06)] flex items-center justify-center text-xs font-semibold text-[#104A4F] flex-shrink-0">
                    {initials}
                  </div>
                )}
                <div>
                  <div className="text-[13px] font-semibold text-[#1A1D21]">{instructorName}</div>
                  {instructorOrg && <div className="text-xs text-[#8B919E]">{instructorOrg}</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        className={`border-t border-[#E4E7EA] px-3.5 py-2 sm:px-4 flex items-center ${additionalCount > 0 ? 'justify-between' : 'justify-end'}`}
      >
        {additionalCount > 0 && !expanded && (
          <span className="text-[11px] text-[#8B919E]">
            <b className="font-semibold text-[#5E6470]">+{additionalCount}</b> more date
            {additionalCount > 1 ? 's' : ''}
          </span>
        )}
        {additionalCount > 0 && expanded && (
          <span className="text-[11px] text-[#8B919E]">
            <b className="font-semibold text-[#5E6470]">+{additionalCount}</b> more:{' '}
            {event.additional_dates.join(', ')}
          </span>
        )}
        {event.url ? (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-[#104A4F] inline-flex items-center gap-0.5 hover:gap-1.5 transition-[gap] duration-150"
          >
            {linkCta}
            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-[#104A4F] inline-flex items-center gap-0.5 hover:gap-1.5 transition-[gap] duration-150"
          >
            {expanded ? 'Less' : 'Details'}
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(EventCard);
